import React, { ReactNode } from 'react';
import { Lexer, Marked } from 'marked';
import type { StyleProp, TextStyle } from 'react-native';
import LaTeXText from '../components/LaTeXText';
import { interSemiBold } from '../lib/fonts';
import { containsLatex } from './latexParser';

// Tokenize emphasis without interpreting math as Markdown or rendering HTML.
// Use a private parser so article rendering keeps its own Markdown options.
const markdown = new Marked({
    extensions: [{
        name: 'summaryMath',
        level: 'inline',
        start: (source) => source.indexOf('$'),
        tokenizer(source) {
            const match = /^(?:\$\$[^$]+?\$\$|\$[^$]+?\$)/.exec(source);
            if (match) return { type: 'summaryMath', raw: match[0] };
        },
    }],
});

interface SummaryTextSegment {
    content: string;
    start: number;
    end: number;
    bold: boolean;
}

export function parseSummaryText(text: string): SummaryTextSegment[] {
    let offset = 0;
    const segments: SummaryTextSegment[] = [];
    new Lexer(markdown.defaults).inlineTokens(text).forEach((token) => {
        const bold = token.type === 'strong';
        const start = offset + (bold ? 2 : 0);
        offset += token.raw.length;
        const end = offset - (bold ? 2 : 0);
        const previous = segments[segments.length - 1];
        // Keep math and surrounding prose together for the existing renderer.
        if (previous && previous.bold === bold && previous.end === start) {
            previous.end = end;
            previous.content += text.slice(start, end);
        } else {
            segments.push({ content: text.slice(start, end), start, end, bold });
        }
    });
    return segments;
}

/**
 * Render a range using offsets in the original source. Parsing the whole field
 * first keeps emphasis intact when a citation boundary falls inside bold text.
 */
export function renderSummaryTextRange(
    segments: SummaryTextSegment[],
    start: number,
    end: number,
    keyPrefix: string,
    style?: StyleProp<TextStyle>,
    fontsLoaded = false,
): ReactNode[] {
    const elements: ReactNode[] = [];
    segments.forEach((segment, index) => {
        const from = Math.max(start, segment.start);
        const to = Math.min(end, segment.end);
        if (from >= to) return;
        const content = segment.content.slice(from - segment.start, to - segment.start);
        if (segment.bold || containsLatex(content)) {
            elements.push(
                <LaTeXText
                    key={`${keyPrefix}-${index}`}
                    text={content}
                    style={segment.bold
                        ? [style, { fontWeight: '600' }, fontsLoaded && interSemiBold]
                        : style}
                    fontsLoaded={fontsLoaded}
                />
            );
        } else {
            elements.push(content);
        }
    });
    return elements;
}
